// Quick guide — Irregular verbs (present tense), grouped by the pattern of their
// irregularity. Most "irregular" verbs aren't random — they fall into a few
// families. Each family now shows a FULL model conjugation so the learner sees
// the whole shape, not just the odd form. Hand-drawn paper style with audio.
import { speakButtonHTML } from "../lib/audio.js";

const PRONOUNS = ["io", "tu", "lui / lei", "noi", "voi", "loro"];

// A full 6-form conjugation table. `hi` is the set of row indices to highlight
// (the irregular / "boot" forms); highlighted rows get the rose treatment.
function conjTable(forms, hi) {
  const set = new Set(hi || []);
  const rows = forms
    .map((form, i) => {
      const on = set.has(i);
      return `
        <div class="flex items-baseline gap-2 py-0.5 px-1.5 rounded ${on ? "bg-rose-50" : ""}">
          <span class="w-24 shrink-0 text-sage-500 text-base" style="font-family:Inter,sans-serif">${PRONOUNS[i]}</span>
          <span class="font-bold ${on ? "text-rose-700" : "text-sage-800"}">${form}</span>${speakButtonHTML(form)}
        </div>`;
    })
    .join("");
  return `<div class="mt-1">${rows}</div>`;
}

// A family card: title, note, the model verb's infinitive, and its full table.
function familyCard({ color, title, note, inf, gloss, forms, hi, extra }) {
  return `
    <div class="sketch-box-sm bg-white/30 p-4 mb-4 break-inside-avoid">
      <h3 class="${color} text-2xl sketch-underline inline-block">${title}</h3>
      <p class="text-sm text-sage-500 mb-2 mt-1" style="font-family:Inter,sans-serif">${note}</p>
      <p class="text-lg text-sage-700">Model: <b class="text-sage-800">${inf}</b> <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${gloss}</span>${speakButtonHTML(inf)}</p>
      ${conjTable(forms, hi)}
      ${extra || ""}
    </div>`;
}

// Boot families — one model verb each, boot forms highlighted. The boot is
// io · tu · lui · loro (everything except noi / voi = rows 0,1,2,5).
const BOOT = [0, 1, 2, 5];

const STEM_FAMILIES = [
  {
    color: "text-emerald-700",
    title: "e → ie",
    note: "The stem e becomes ie inside the boot; noi / voi keep the e.",
    inf: "sedere",
    gloss: "to sit",
    forms: ["siedo", "siedi", "siede", "sediamo", "sedete", "siedono"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "o → uo",
    note: "The stem o becomes uo inside the boot.",
    inf: "morire",
    gloss: "to die",
    forms: ["muoio", "muori", "muore", "moriamo", "morite", "muoiono"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "extra -g- (io & loro)",
    note: "io and loro gain a g; tu / lui also shift e → ie.",
    inf: "tenere",
    gloss: "to hold / keep",
    forms: ["tengo", "tieni", "tiene", "teniamo", "tenete", "tengono"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "-isco verbs",
    note: "Many -ire verbs insert -isc- in the boot.",
    inf: "finire",
    gloss: "to finish",
    forms: ["finisco", "finisci", "finisce", "finiamo", "finite", "finiscono"],
    hi: BOOT,
  },
];

const OTHER_FAMILIES = [
  {
    color: "text-amber-700",
    title: "Contracted infinitives",
    note: "fare, dire, bere keep a fuller Latin stem (fac-, dic-, bev-).",
    inf: "fare",
    gloss: "to do / make",
    forms: ["faccio", "fai", "fa", "facciamo", "fate", "fanno"],
    hi: [0, 1, 2, 3, 4, 5],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same idea: <b>dire</b> → dico, dici, dice, diciamo, dite, dicono · <b>bere</b> → bevo, bevi, beve, beviamo, bevete, bevono.</p>`,
  },
  {
    color: "text-violet-700",
    title: "The modals",
    note: "potere, volere, dovere are irregular across the boot.",
    inf: "potere",
    gloss: "to be able / can",
    forms: ["posso", "puoi", "può", "possiamo", "potete", "possono"],
    hi: [0, 1, 2, 5],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif"><b>volere</b> → voglio, vuoi, vuole, vogliamo, volete, vogliono · <b>dovere</b> → devo, devi, deve, dobbiamo, dovete, devono.</p>`,
  },
  {
    color: "text-sky-700",
    title: "Spelling-keepers (-care / -gare)",
    note: "Add an h before -i / -e endings to keep the hard c / g sound.",
    inf: "cercare",
    gloss: "to look for",
    forms: ["cerco", "cerchi", "cerca", "cerchiamo", "cercate", "cercano"],
    hi: [1, 3],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same idea: <b>pagare</b> → pago, paghi, paga, paghiamo, pagate, pagano.</p>`,
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — essere",
    note: "No pattern — learn the whole shape by heart.",
    inf: "essere",
    gloss: "to be",
    forms: ["sono", "sei", "è", "siamo", "siete", "sono"],
    hi: [0, 1, 2, 3, 4, 5],
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — avere",
    note: "Also fully irregular — the other auxiliary verb.",
    inf: "avere",
    gloss: "to have",
    forms: ["ho", "hai", "ha", "abbiamo", "avete", "hanno"],
    hi: [0, 1, 2, 3, 4, 5],
  },
];

export function irregularVerbsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Irregular Verbs ⚡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Present tense. Most “irregular” verbs aren't random — they belong to a few families. Learn the family and one model verb in full, and the rest fall into line. Rose-tinted rows are the forms that break from the regular pattern.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg mb-6">
        <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Boot verbs (the “boot” 👢)</h3>
        <p class="text-sage-700 mt-1 mb-3">The stem shifts in every form <b>except noi / voi</b>. Shade in the changed forms and they make a boot shape — that's why these are called boot verbs.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${STEM_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · The other families</h3>
        <p class="text-sage-700 mt-1 mb-3">Some keep an older Latin stem, some are spelling-driven, and <b>essere / avere</b> break everywhere.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${OTHER_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">essere · avere · fare · stare get their own deep-dive in <b>The Big 4 Verbs</b>. For any verb's full conjugation across all tenses, use the Verb Index.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
